import React, { useMemo } from 'react';
import { FiCalendar, FiMousePointer, FiArrowUp, FiArrowDown, FiEdit3, FiDownload } from 'react-icons/fi';
import { t } from 'i18next';
import { useFormik } from 'formik';
import { EmptyState } from '@/components/custom/empty-state';
import { Filter, FilterContent, FilterInput, FilterSelectNative, FilterSelectSearch } from '@/components/custom/filter';
import { EssayMonitoringCard } from './essay-monitoring-card';
import toast from 'react-hot-toast';
import { useEffect } from 'react';
import { useState } from 'react';
import { useClient } from 'jsonapi-react';
import { Loader } from '@/components/custom/loader';
import moment from 'moment';
import { statusBadgeText } from '../utils/BadgeEssay';
import ActionCableConsumer from '@/utils/actionCableConsumer';
import { useSession } from '@/hooks/useSession';
import { EssayMonitoringCardMobile } from './essay-monitoring-card-mobile';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import EndScroll from '@/app/components/endScroll';
import { useCheckIncludeProfiles } from '@/hooks/useAuthorityLevel';
import { CourseAbout, CourseAboutBody, CourseAboutDescription, CourseAboutHeader, CourseAboutSubtitle, CourseAboutTitle, CourseBanner, CourseBannerContainer, CourseBannerContent, CourseBannerTitle } from '@/components/custom/course';
import { Badge } from '@/components/ui/badge';
import { CardProgress, CardProgressBar, CardProgressContainer } from '@/components/ui/card';
import { DashboardContent } from '@/layouts/dashboard';
import { CardMetric, CardMetricBody, CardMetricHeader, CardMetricTitle, CardMetricValue, CardMetricWrapper } from '@/components/ui/card-metric';
import { Button, ButtonIcon } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableHeader, TableBody, TableRow, TableHead } from '@/components/ui/table';

export function EssayMonitoring({ essayId, uri }) {
  const [essay, setEssay] = useState({});
  const [loading, setLoading] = useState(false);
  const [webhookNotification, setWebhookNotification] = useState(null);
  const [selectedTab, setSelectedTab] = useState('redacoes');
  const [institutions, setInstitutions] = useState([]);
  const [courses, setCourses] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const { session } = useSession();
  const isStudent = useCheckIncludeProfiles({ only: ['student'] });

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });

  const client = useClient();

  const filters = useFormik({
    initialValues: {
      status: 'blank',
      searchTerm: '',
      institution: null,
      course: null,
      classroom: null
    }
  });

  useEffect(() => {
    if (isStudent) {
      window.location.href = `/redacao/sobre/${essayId}`;
    }
  }, []);

  // Memoiza os filtros para evitar re-criação a cada render
  const memoizedFilters = useMemo(
    () => ({
      ...(filters.values.institution?.value &&
        filters.values.institution.value !== 'blank' && {
          institution_id: filters.values.institution.value
        }),
      ...(filters.values.course?.value &&
        filters.values.course.value !== 'blank' && {
          course_id: filters.values.course.value
        }),
      ...(filters.values.classroom?.value &&
        filters.values.classroom.value !== 'blank' && {
          classroom_id: filters.values.classroom.value
        }),
      ...(filters.values.status &&
        filters.values.status !== 'blank' && {
          status: filters.values.status
        })
    }),
    [filters.values.institution?.value, filters.values.course?.value, filters.values.classroom?.value, filters.values.status]
  );

  // Hook de infinite scroll com paginação virtual
  const {
    data: essayStudents,
    loading: dataLoading,
    initialLoading,
    hasMore,
    loadingNextPage, // Estado para mostrar loader ao carregar próxima página
    setData: setEssayStudents,
    sentinelRef
  } = useInfiniteScroll({
    endpoint: `essays/${essayId}/essay_students`,
    pageSize: 50,
    filters: memoizedFilters,
    searchTerm: filters.values.searchTerm,
    searchFields: ['user.name', 'user.email'],
    debounceMs: 300,
    onError: () => toast.error('Erro ao buscar estudantes')
  });

  const getEssay = async () => {
    const { data, error } = await client.fetch(['essays', essayId]);
    if (error) {
      toast.error('Erro ao buscar redação');
    } else {
      setEssay(data);
    }
  };

  const fetchInstitutions = async (url = '/institutions') => {
    const { data, error } = await client.fetch(url);
    if (error) {
      toast.error('Falha ao carregar instituições');
    } else {
      setInstitutions(data.map(item => ({ value: item.id, label: item.name })));
    }
  };

  const fetchCourses = async (url = '/courses/simple') => {
    const { data, error } = await client.fetch(url);
    if (error) {
      toast.error('Falha ao carregar cursos');
    } else {
      setCourses(data.map(item => ({ value: item.id, label: item.title })));
    }
  };

  const fetchClassrooms = async (url = '/classrooms/simple') => {
    if (filters.values.course !== null && filters.values.course.value !== 'blank' && filters.values.institution !== null && filters.values.institution.value !== 'blank') {
      let endpoint = `/classrooms/simple?filter[course_id]=${filters?.values?.course?.value}&filter[institution_id]=${filters?.values?.institution?.value}`;

      const { data, error } = await client.fetch(endpoint);
      if (error) {
        toast.error('Falha ao carregar turmas');
      } else {
        setClassrooms(data.map(item => ({ value: item.id, label: item.title })));
      }
    }
  };

  useEffect(() => {
    setLoading(true);
    Promise.allSettled([getEssay(), fetchInstitutions(), fetchCourses()]).finally(() => {
      setLoading(false);
    });
  }, []);

  const options = [
    { name: 'Correção por Professor', id: 'reviewed' },
    { name: 'Correção por IA', id: 'unreviewed' },
    { name: 'Em revisão', id: 'reviewing' },
    { name: 'Em processamento', id: 'processing' },
    { name: 'Anulada pela IA', id: 'annulled' },
    { name: 'Anulada pelo professor', id: 'annulled_by_teacher' },
    { name: 'Aluno não enviou', id: 'not_sent' },
    { name: 'Aceitando respostas', id: 'accepting_proposal' },
    { name: 'Arquivo inválido', id: 'minimum_length_not_satisfied' },
    { name: 'Falha ao processar', id: 'failed_to_process' },
    { name: 'Prazo encerrado', id: 'close_for_submissions' },
    { name: 'Aguardando envio do texto', id: 'ocr_review' }
  ];

  const breadcrumbs = {
    title: 'Atividades',
    nav: [
      {
        route: '/redacao',
        name: 'Redação',
        isActive: false
      },
      {
        route: uri,
        name: essay?.topic?.length > 70 ? essay?.topic?.slice(0, 70) + '...' : essay?.topic,
        isActive: true
      }
    ]
  };

  useEffect(() => {
    if (!webhookNotification) return;

    const setNewState = (item, socket) => {
      if ((socket?.id && item?.id === socket?.id) || item?.user?.id === socket?.user_id) {
        return { ...item, status: socket?.status, corrector: socket?.corrector, grade: socket?.grade, id: socket?.id };
      }
      return item;
    };

    // CORREÇÃO: Usa função callback para acessar TODOS os dados (allData do hook)
    // prevAllData contém os 22.529 registros completos, não apenas os exibidos
    // Isso evita sobrescrever o array completo com apenas os dados paginados
    setEssayStudents(prevAllData => {
      console.log('[WebSocket] Atualizando registro via WebSocket. Total de registros:', prevAllData.length);
      return prevAllData.map(item => setNewState(item, webhookNotification));
    });
    setWebhookNotification(null);
  }, [webhookNotification, setEssayStudents]);

  // const filteredUsersByName = essayStudents.filter(item => {
  //   if (filters.values.searchTerm === '') return essayStudents;
  //   return item.user.name.toLowerCase().includes(filters.values.searchTerm.toLowerCase());
  // });

  const handleReceivedNotification = data => {
    setWebhookNotification(data);
  };

  const handleSort = key => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const sortedUsers = useMemo(() => {
    const sortableUsers = [...essayStudents];
    if (sortConfig.key !== null) {
      sortableUsers.sort((a, b) => {
        let aValue, bValue;

        switch (sortConfig.key) {
          case 'name':
            aValue = a.user.name ?? '';
            bValue = b.user.name ?? '';
            break;
          case 'entrega':
            aValue = new Date(a['delivery-date']) ?? new Date(0);
            bValue = new Date(b['delivery-date']) ?? new Date(0);
            break;
          case 'status':
            aValue = a.status ?? '';
            bValue = b.status ?? '';
            break;
          case 'nota':
            aValue = a.grade ?? 0;
            bValue = b.grade ?? 0;
            break;
          case 'corrigidoPor':
            aValue = a?.corrector?.name ?? '';
            bValue = b?.corrector?.name ?? '';
            break;
          default:
            return 0;
        }

        if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
        return 0;
      });
    }
    return sortableUsers;
  }, [sortConfig, essayStudents]);

  useEffect(() => {
    fetchClassrooms();
  }, [filters.values.course]);

  function downloadFile() {
    const url = `${process.env.REACT_APP_DEV_URL}/reports/essays/${essayId}/report.xlsx?`;
    let params = new URLSearchParams();
    if (filters.values.classroom) {
      params.set('classroom_id', filters.values.classroom.value);
    }
    if (filters.values.institution) {
      params.set('institution_id', filters.values.institution.value);
    }
    if (filters.values.course) {
      params.set('course_id', filters.values.course.value);
    }

    fetch(`${url}${params.toString()}`, {
      headers: {
        Authorization: `Bearer ${session?.user?.authToken}`
      }
    })
      .then(response => {
        if (!response.ok) {
          toast.error(`Erro ao baixar o relatório`);
        }
        return response.blob();
      })
      .then(blob => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `detalhamento-${essay?.topic}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
      })
      .catch(error => console.error('Erro no download:', error));
  }

  const statusBadgeColor = {
    accepting_proposal: 'attention',
    close_for_submissions: 'neutral',
    processing: 'warning',
    unreviewed: 'success',
    reviewed: 'success',
    annulled: 'danger',
    annulled_by_teacher: 'danger ',
    minimum_length_not_satisfied: 'danger',
    failed_to_process: 'danger',
    reviewing: 'warning',
    not_sent: 'neutral',
    ocr_review: 'warning',
    sending_by_teacher: 'warning'
  };

  const summaryWidgets = [
    {
      title: 'Índice de envio',
      value: essay?.['submissions-percent'],
      icon: <FiMousePointer />
    },
    {
      title: 'Nota média',
      value: essay?.['average-grade'],
      icon: <FiCalendar />
    },
    {
      title: 'Prazo de entrega',
      value: essay?.['delivery-date'] ? moment(essay['delivery-date']).format('DD/MM/YYYY') : '--/--/----',
      icon: <FiCalendar />
    },
    // Exemplo de condicional caso queira incluir o de envios por estudante aqui também:
    ...(essay?.['allow-multiple-submissions']
      ? [
          {
            title: 'Envio por estudante',
            value: '2 envios', // Ou a lógica vinda da API
            icon: <FiEdit3 />
          }
        ]
      : [])
  ];

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      <ActionCableConsumer
        channel="EssayStudentChannel"
        setObjectChannel={handleReceivedNotification}
        channelKey={'essay_id'}
        channelId={essayId}
      />

      <CourseAbout
        className="mb-6"
        cardBodyClassName="gap-y-8"
      >
        <CourseBanner className="min-h-[224px]">
          <CourseBannerContent>
            <CourseBannerContainer>
              <Badge
                size="xs"
                variant={moment(essay?.['publish-at']).isAfter(moment()) ? 'warning' : statusBadgeColor[essay.status]}
              >
                {moment(essay?.['publish-at']).isAfter(moment()) ? 'Agendada' : statusBadgeText[essay.status]}
              </Badge>
              <CourseBannerTitle title={essay?.topic}>{essay?.topic?.length > 70 ? essay?.topic?.slice(0, 70) + '...' : essay?.topic}</CourseBannerTitle>
            </CourseBannerContainer>
          </CourseBannerContent>
        </CourseBanner>

        <CardMetricWrapper>
          {summaryWidgets.map((widget, index) => (
            <CardMetric
              key={`widget-${index}`}
              variant="outline"
            >
              <CardMetricHeader>
                <CardMetricTitle>{widget.title}</CardMetricTitle>
              </CardMetricHeader>

              <CardMetricBody>
                <CardMetricValue>
                  {widget.icon} {widget.value ?? '--'}
                </CardMetricValue>
              </CardMetricBody>
            </CardMetric>
          ))}
        </CardMetricWrapper>

        <CourseAboutBody className="gap-0">
          <CourseAboutTitle>Envios</CourseAboutTitle>

          <CardProgress className="flex-wrap-reverse">
            <CardProgressContainer>
              <CardProgressBar percentage={essay?.['submissions-percent']} />
            </CardProgressContainer>
            <p className="mb-0 shrink-0">{`${essay?.['submissions-count'] >= 0 ? essay?.['submissions-count'] : '--'}/${essay?.['total-of-students'] >= 0 ? essay?.['total-of-students'] : '--'} `}</p>
          </CardProgress>
        </CourseAboutBody>
      </CourseAbout>

      <Tabs
        value={selectedTab}
        onValueChange={setSelectedTab}
        className="w-full"
      >
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <TabsList>
            <TabsTrigger value="redacoes">Redações</TabsTrigger>
            <TabsTrigger value="sobre">Sobre</TabsTrigger>
          </TabsList>

          <Button
            variant="outline"
            type="button"
            onClick={downloadFile}
          >
            <ButtonIcon>
              <FiDownload />
            </ButtonIcon>{' '}
            Exportar relatório detalhado por aluno
          </Button>
        </div>

        <TabsContent value="redacoes">
          <CourseAbout>
            <CourseAboutBody>
              <Filter variant="plain">
                <FilterContent>
                  <FilterInput
                    label={t('textsCommon.search')}
                    placeholder="Pesquisar por estudante"
                    type="search"
                    name="search"
                    value={filters.values.searchTerm}
                    onChange={e => filters.setFieldValue('searchTerm', e.target.value)}
                  />

                  <FilterSelectNative
                    blankLabel={t('filter.blankLabelAll2')}
                    label={t('filter.labelSelectStatus')}
                    value={filters.values.status}
                    onChange={e => filters.setFieldValue('status', e.target.value)}
                    options={options}
                  />

                  <FilterSelectSearch
                    label="Instituição"
                    options={institutions}
                    value={filters.values.institution ?? { value: 'blank', label: 'Todos' }}
                    setValue={selectedOption => {
                      const normalizedValue = selectedOption?.value === 'blank' ? null : selectedOption;

                      filters.setFieldValue('institution', normalizedValue);
                      filters.setFieldValue('course', null);
                      filters.setFieldValue('classroom', null);
                    }}
                  />

                  <FilterSelectSearch
                    label="Curso"
                    options={courses}
                    value={filters.values.course ?? { value: 'blank', label: 'Todos' }}
                    setValue={selectedOption => {
                      const normalizedValue = selectedOption?.value === 'blank' ? null : selectedOption;

                      filters.setFieldValue('course', normalizedValue);
                      filters.setFieldValue('classroom', null);
                    }}
                  />

                  <FilterSelectSearch
                    label="Turma"
                    options={classrooms}
                    value={filters.values.classroom ?? { value: 'blank', label: 'Todos' }}
                    setValue={selectedOption => {
                      const normalizedValue = selectedOption?.value === 'blank' ? null : selectedOption;

                      filters.setFieldValue('classroom', normalizedValue);
                    }}
                  />
                </FilterContent>
              </Filter>

              {initialLoading ? (
                <Loader />
              ) : !dataLoading && sortedUsers.length === 0 ? (
                <EmptyState
                  type="content"
                  title={t('emptyState.noItemFound')}
                />
              ) : (
                <div>
                  <div className="flex flex-col gap-5 md:hidden">
                    {sortedUsers?.map(student => (
                      <EssayMonitoringCardMobile
                        currentUser={session.user.id}
                        essay={essay}
                        key={student?.user?.id}
                        student={student}
                      />
                    ))}
                  </div>

                  <Table className="hidden min-w-[1200px] md:table">
                    <TableHeader>
                      <TableRow>
                        <TableHead
                          className="cursor-pointer"
                          onClick={() => handleSort('name')}
                          style={{ fontWeight: sortConfig.key === 'name' ? 'bold' : 'normal' }}
                        >
                          Nome {sortConfig.key === 'name' && (sortConfig.direction === 'ascending' ? <FiArrowUp /> : <FiArrowDown />)}
                        </TableHead>
                        <TableHead className="w-[80px] cursor-pointer">Envios {sortConfig.key === 'envios' && (sortConfig.direction === 'ascending' ? <FiArrowUp /> : <FiArrowDown />)}</TableHead>
                        <TableHead
                          className="w-[116px] cursor-pointer"
                          onClick={() => handleSort('entrega')}
                          style={{ fontWeight: sortConfig.key === 'entrega' ? 'bold' : 'normal' }}
                        >
                          Entrega {sortConfig.key === 'entrega' && (sortConfig.direction === 'ascending' ? <FiArrowUp /> : <FiArrowDown />)}
                        </TableHead>
                        <TableHead
                          className="w-[82px] cursor-pointer"
                          onClick={() => handleSort('nota')}
                          style={{ fontWeight: sortConfig.key === 'nota' ? 'bold' : 'normal' }}
                        >
                          Nota {sortConfig.key === 'nota' && (sortConfig.direction === 'ascending' ? <FiArrowUp /> : <FiArrowDown />)}
                        </TableHead>
                        <TableHead
                          className="w-[180px] cursor-pointer"
                          onClick={() => handleSort('status')}
                          style={{ fontWeight: sortConfig.key === 'status' ? 'bold' : 'normal' }}
                        >
                          Status da correção {sortConfig.key === 'status' && (sortConfig.direction === 'ascending' ? <FiArrowUp /> : <FiArrowDown />)}
                        </TableHead>
                        <TableHead
                          className="w-[226px] cursor-pointer"
                          onClick={() => handleSort('corrigidoPor')}
                          style={{ fontWeight: sortConfig.key === 'corrigidoPor' ? 'bold' : 'normal' }}
                        >
                          Corrigido por {sortConfig.key === 'corrigidoPor' && (sortConfig.direction === 'ascending' ? <FiArrowUp /> : <FiArrowDown />)}
                        </TableHead>
                        <TableHead className="w-[370px]">Ações</TableHead>
                      </TableRow>
                    </TableHeader>

                    <TableBody>
                      {sortedUsers?.map((student, i) => (
                        <EssayMonitoringCard
                          currentUser={session.user.id}
                          essay={essay}
                          key={i}
                          student={student}
                        />
                      ))}
                    </TableBody>
                  </Table>

                  <div
                    ref={sentinelRef}
                    className="my-5 flex h-5 items-center justify-center"
                  >
                    {(dataLoading || loadingNextPage) && <Loader />}
                  </div>

                  {!hasMore && !loadingNextPage && sortedUsers.length > 0 && <EndScroll />}
                </div>
              )}
            </CourseAboutBody>
          </CourseAbout>
        </TabsContent>

        <TabsContent value="sobre">
          <CourseAbout>
            <CourseAboutHeader>
              <CourseAboutTitle>Enunciado</CourseAboutTitle>

              <CourseAboutDescription className="whitespace-pre-wrap">{essay?.statement}</CourseAboutDescription>

              <CourseAboutDescription>Tema: {essay?.topic}</CourseAboutDescription>
            </CourseAboutHeader>

            <CourseAboutBody>
              <CourseAboutTitle>Textos motivadores</CourseAboutTitle>

              {essay?.['essay-motivator-texts']?.map((item, index) => {
                return (
                  <article key={index}>
                    <CourseAboutSubtitle>Texto {index + 1}</CourseAboutSubtitle>
                    <CourseAboutDescription
                      className="course-about__description"
                      dangerouslySetInnerHTML={{ __html: item.body }}
                    />
                  </article>
                );
              })}
            </CourseAboutBody>
          </CourseAbout>
        </TabsContent>
      </Tabs>
    </DashboardContent>
  );
}
